import React, { useState, useRef, useEffect } from 'react';
import { getFullApiUrl } from '../config';

const FloatingChatbot = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Halo! 👋 Saya Tabulara AI Assistant. Ada yang bisa saya bantu tentang fitur-fitur di aplikasi ini?',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const chatWindowRef = useRef(null);
  const buttonRef = useRef(null);

  // Get LLM configuration from localStorage
  const llmConfig = JSON.parse(localStorage.getItem('llmConfig') || '{}');
  const insightsProvider = llmConfig.insightsProvider || 'gemini';
  const insightsModel = llmConfig[`${insightsProvider}Model`] || (insightsProvider === 'groq' ? 'llama-3.1-8b-instant' : 'gemini-2.0-flash');
  const providerApiKey = insightsProvider === 'groq' ? llmConfig.groqApiKey : llmConfig.geminiApiKey;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load saved position from localStorage
  useEffect(() => {
    const savedPosition = localStorage.getItem('chatbotPosition');
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition);
        setPosition(pos);
      } catch (e) {
        console.error('Error loading chatbot position:', e);
      }
    }
  }, []);

  // Save position to localStorage when it changes
  useEffect(() => {
    if (position.x !== null && position.y !== null) {
      localStorage.setItem('chatbotPosition', JSON.stringify(position));
    }
  }, [position]);

  // Handle drag start
  const handleDragStart = (e) => {
    if (chatWindowRef.current) {
      const rect = chatWindowRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  // Handle drag
  useEffect(() => {
    const handleDrag = (e) => {
      if (!isDragging) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Keep within viewport bounds
      const maxX = window.innerWidth - (chatWindowRef.current?.offsetWidth || 384);
      const maxY = window.innerHeight - (chatWindowRef.current?.offsetHeight || 600);
      const minX = 0;
      const minY = 0;

      const constrainedX = Math.max(minX, Math.min(maxX, newX));
      const constrainedY = Math.max(minY, Math.min(maxY, newY));

      setPosition({ x: constrainedX, y: constrainedY });
    };

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleDrag);
      document.addEventListener('mouseup', handleDragEnd);
      document.body.style.userSelect = 'none'; // Prevent text selection while dragging
    }

    return () => {
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleDragEnd);
      document.body.style.userSelect = '';
    };
  }, [isDragging, dragOffset]);

  // Calculate chat window position
  const getChatWindowStyle = () => {
    if (position.x !== null && position.y !== null) {
      return {
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: 'auto',
        bottom: 'auto'
      };
    }
    return {};
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Get chat history (last 10 messages)
      const chatHistory = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Call backend API
      const response = await fetch(getFullApiUrl('AI_ASSISTANT'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          chat_history: chatHistory,
          provider: insightsProvider,
          model: insightsModel,
          api_key: providerApiKey
        }),
      });

      const data = await response.json();

      if (data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          suggested_menu: data.suggested_menu,
          action: data.action
        };

        setMessages(prev => [...prev, assistantMessage]);

        // If there's a suggested menu, show navigation button
        if (data.suggested_menu && onNavigate) {
          // Auto-navigation will be handled by user clicking the button
        }
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Maaf, saya sedang mengalami masalah teknis. Silakan coba lagi. 😔',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format message text dengan proper styling
  const formatMessage = (text) => {
    // Split by lines
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];

    lines.forEach((line, lineIndex) => {
      // Check if it's a bullet point
      if (line.trim().startsWith('*') || line.trim().startsWith('-')) {
        const content = line.trim().substring(1).trim();
        // Format bold text within bullet
        const formattedContent = formatBoldText(content);
        listItems.push(
          <li key={`li-${lineIndex}`} className="ml-4">
            {formattedContent}
          </li>
        );
      } else {
        // If we have accumulated list items, render them first
        if (listItems.length > 0) {
          elements.push(
            <ul key={`ul-${lineIndex}`} className="list-disc list-outside my-2 space-y-1">
              {listItems}
            </ul>
          );
          listItems = [];
        }

        // Check if line is a header (starts with ##)
        if (line.trim().startsWith('##')) {
          const headerText = line.trim().substring(2).trim();
          elements.push(
            <div key={`h-${lineIndex}`} className="font-bold text-base mt-2 mb-1">
              {formatBoldText(headerText)}
            </div>
          );
        } else if (line.trim()) {
          // Regular paragraph with bold formatting
          elements.push(
            <p key={`p-${lineIndex}`} className="mb-1">
              {formatBoldText(line)}
            </p>
          );
        } else {
          // Empty line for spacing
          elements.push(<div key={`br-${lineIndex}`} className="h-1"></div>);
        }
      }
    });

    // Add remaining list items if any
    if (listItems.length > 0) {
      elements.push(
        <ul key="ul-final" className="list-disc list-outside my-2 space-y-1">
          {listItems}
        </ul>
      );
    }

    return <div className="space-y-1">{elements}</div>;
  };

  // Format bold text (**text** -> <strong>)
  const formatBoldText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index} className="font-bold">{part.slice(2, -2)}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  const handleNavigate = (menuId) => {
    if (onNavigate) {
      onNavigate(menuId);
      setIsOpen(false);
    }
  };

  const quickActions = [
    { label: '📊 Analisis Bank Statement', message: 'Bagaimana cara analisis bank statement?' },
    { label: '💼 Laporan Keuangan', message: 'Cara analisis laporan keuangan perusahaan?' },
    { label: '🔧 Konfigurasi LLM', message: 'Bagaimana cara ubah model AI yang digunakan?' },
    { label: '❓ Lihat Semua Fitur', message: 'Apa saja fitur yang tersedia di Tabulara?' }
  ];

  return (
    <>
      {/* Floating Chat Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 right-6 w-16 h-16 rounded-full bg-primary-600 text-white shadow-lg hover:shadow-xl hover:bg-primary-700 transform transition-all duration-300 z-50 flex items-center justify-center ${
          isOpen ? 'scale-0' : 'scale-100'
        }`}
      >
        <i className="fa-solid fa-robot text-2xl"></i>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          ref={chatWindowRef}
          style={getChatWindowStyle()}
          className={`fixed w-96 h-[600px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col z-50 transition-colors duration-300 ${position.x === null ? 'bottom-20 right-6' : ''} ${isDragging ? 'cursor-move' : ''} select-none`}
        >
          {/* Header - Draggable Area */}
          <div 
            onMouseDown={handleDragStart}
            className="bg-primary-600 dark:bg-primary-700 text-white p-4 rounded-t-2xl flex items-center justify-between cursor-move select-none transition-colors duration-300"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center mr-3 transition-colors duration-300">
                <i className="fa-solid fa-robot text-primary-600 dark:text-primary-400 text-xl transition-colors duration-300"></i>
              </div>
              <div>
                <h3 className="font-semibold">Tabulara AI Assistant</h3>
                <p className="text-xs text-primary-100 dark:text-primary-200">Ditenagai oleh AI</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 dark:hover:bg-gray-600/30 rounded-full p-2 transition-colors"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>

          {/* Messages Container */}
          <div
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900 transition-colors duration-300"
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 transition-colors duration-300 ${
                    message.role === 'user'
                      ? 'bg-primary-600 dark:bg-primary-700 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-white shadow-sm'
                  }`}
                >
                  <div className="text-sm">
                    {message.role === 'user' ? (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    ) : (
                      formatMessage(message.content)
                    )}
                  </div>
                  
                  {/* Navigation Button */}
                  {message.suggested_menu && (
                    <button
                      onClick={() => handleNavigate(message.suggested_menu)}
                      className="mt-2 w-full bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50 text-primary-700 dark:text-primary-300 px-3 py-2 rounded-lg text-xs font-medium flex items-center justify-center transition-colors"
                    >
                      <i className="fa-solid fa-arrow-right mr-2"></i>
                      Buka Menu Ini
                    </button>
                  )}
                  
                  <p className="text-xs mt-1 opacity-70 dark:opacity-60">
                    {message.timestamp.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl px-4 py-3 shadow-sm transition-colors duration-300">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length === 1 && (
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
              <p className="text-xs text-gray-600 dark:text-gray-300 mb-2 transition-colors duration-300">Coba tanya:</p>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setInputMessage(action.message);
                    }}
                    className="text-left text-xs bg-white dark:bg-gray-800 hover:bg-primary-50 dark:hover:bg-primary-900/30 text-gray-700 dark:text-gray-300 px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-500 transition-colors"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-b-2xl transition-colors duration-300">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Tanya sesuatu..."
                className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm transition-colors duration-300"
                disabled={isTyping}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isTyping}
                className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
              >
                <i className="fa-solid fa-paper-plane"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingChatbot;

